import { Collapsible, CollapsibleContent } from '@radix-ui/react-collapsible';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import {
  AlertOctogonalIcon,
  AlertTriangleIcon,
  CancelIcon,
  CircleCheckIcon,
  InfoIcon,
} from '../icons';
import { type AutoneIconProps } from '../icons/factory/create-icon-component';
import { cn } from '../utils';

import { Button } from './Button';

const alertVariants = cva(
  'relative w-full border rounded-sm p-4 text-sm grid has-[>svg]:grid-cols-[max-content_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-6 [&>svg]:translate-y-0.5 [&>svg]:text-current *:data-[slot=alert-description]:text-muted-foreground shadow-sm',
  {
    variants: {
      variant: {
        default:
          'bg-muted border-muted-foreground/20 [&>svg]:text-muted-foreground',
        destructive:
          'bg-destructive/10 border-destructive/20 [&>svg]:text-destructive',
        success: 'bg-success/10 border-success/20 [&>svg]:text-success',
        warning: 'bg-warning/10 border-warning/20 [&>svg]:text-warning',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);
interface AlertProps extends React.ComponentProps<'div'> {
  variant: VariantProps<typeof alertVariants>['variant'];
  children: React.ReactNode;
  isCollapsible?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function Alert({
  className,
  variant,
  children,
  isCollapsible,
  open,
  onOpenChange,
  ...props
}: AlertProps) {
  const [contrlledIsOpen, controllableSetOpen] = useControllableState({
    defaultProp: true,
    prop: open,
    onChange: onOpenChange,
  });

  return (
    <Collapsible open={contrlledIsOpen} onOpenChange={onOpenChange}>
      <CollapsibleContent className="transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div
          key="alert"
          data-slot="alert"
          role="alert"
          className={cn(alertVariants({ variant }), className)}
          {...props}
        >
          {children}
          {isCollapsible && (
            <Button
              aria-label="Close alert"
              id="close-alert"
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 aspect-square hover:bg-transparent"
              onClick={() => controllableSetOpen(false)}
            >
              <CancelIcon />
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
const AlertIcon = ({
  variant,
  ...props
}: {
  variant: VariantProps<typeof alertVariants>['variant'];
} & Omit<AutoneIconProps, 'ref'>) => {
  switch (variant) {
    case 'success':
      return <CircleCheckIcon {...props} />;
    case 'warning':
      return <AlertTriangleIcon {...props} />;
    case 'destructive':
      return <AlertOctogonalIcon {...props} />;
    default:
      return <InfoIcon {...props} />;
  }
};

function AlertTitle({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium text-lg tracking-tight',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertIcon };
